import { useEffect, useRef, useState } from "react";
import Spinner from "./Spinner";
import styles from "../../PricingTool.module.css";

export interface AddressResult {
  label: string;
  lat: number;
  lng: number;
  /** ISO-3166-1 alpha-2, when the geocoder resolved one. */
  country?: string;
}

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting?: { main_text: string; secondary_text: string };
}

interface Props {
  /** Currently selected address label. */
  value: string;
  onSelect: (r: AddressResult) => void;
  placeholder?: string;
  /** Whether the Google Maps "places" library has loaded. */
  ready: boolean;
}

// Lightweight Google Places autocomplete styled to match the Combo dropdown.
export default function AddressAutocomplete({
  value,
  onSelect,
  placeholder = "Search address…",
  ready,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(false);

  const serviceRef = useRef<any>();
  const tokenRef = useRef<any>();
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const g = (window as any).google;
    if (ready && g?.maps?.places && !serviceRef.current) {
      serviceRef.current = new g.maps.places.AutocompleteService();
      tokenRef.current = new g.maps.places.AutocompleteSessionToken();
    }
  }, [ready]);

  const runSearch = (q: string) => {
    setSearch(q);
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim() || !serviceRef.current) {
      setPredictions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(() => {
      serviceRef.current.getPlacePredictions(
        { input: q, sessionToken: tokenRef.current },
        (res: Prediction[] | null) => {
          setPredictions(res || []);
          setLoading(false);
        }
      );
    }, 300);
  };

  const pick = (p: Prediction) => {
    const g = (window as any).google;
    const geocoder = new g.maps.Geocoder();
    geocoder.geocode(
      { placeId: p.place_id },
      (results: any[], status: string) => {
        if (status === "OK" && results && results[0]) {
          const loc = results[0].geometry.location;
          const country = (results[0].address_components || []).find((c: any) =>
            (c.types || []).includes("country")
          )?.short_name;
          onSelect({
            label: p.description,
            lat: loc.lat(),
            lng: loc.lng(),
            country,
          });
        }
      }
    );
    // Start a fresh session after a selection (billing best-practice).
    tokenRef.current = new g.maps.places.AutocompleteSessionToken();
    setOpen(false);
  };

  return (
    <div className={styles.combo_wrapper}>
      {open && (
        <div className={styles.combo_overlay} onClick={() => setOpen(false)} />
      )}
      <div
        className={`${styles.combo_trigger} ${open ? styles.combo_trigger_open : ""}`}
        onClick={() => {
          setOpen(true);
          setSearch("");
          setPredictions([]);
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(0,0,0,0.35)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        {open ? (
          <input
            autoFocus
            className={styles.combo_search_input}
            value={search}
            placeholder={placeholder}
            onChange={(e) => runSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={styles.combo_trigger_text}>
            {value || placeholder}
          </span>
        )}
        {loading && open ? (
          <Spinner size={14} />
        ) : (
          <span className={styles.combo_trigger_caret}>▾</span>
        )}
      </div>
      {open && (
        <div className={styles.combo_dropdown}>
          {!ready ? (
            <div
              style={{ padding: 10, fontSize: 12, color: "rgba(0,0,0,0.4)" }}
            >
              Loading maps…
            </div>
          ) : loading ? (
            <div
              style={{
                padding: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#6B4EFF",
                fontSize: 12,
              }}
            >
              <Spinner size={14} /> Searching…
            </div>
          ) : predictions.length === 0 ? (
            <div
              style={{ padding: 10, fontSize: 12, color: "rgba(0,0,0,0.4)" }}
            >
              {search ? "No matches" : "Start typing an address"}
            </div>
          ) : (
            predictions.map((p) => (
              <button
                key={p.place_id}
                className={styles.combo_option}
                onClick={() => pick(p)}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "rgba(0,0,0,0.88)",
                  }}
                >
                  {p.structured_formatting?.main_text || p.description}
                </span>
                {p.structured_formatting?.secondary_text && (
                  <span style={{ fontSize: 10, color: "rgba(0,0,0,0.45)" }}>
                    {p.structured_formatting.secondary_text}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
